import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';

import { FeatureDTO } from 'src/app/interfaces/feature';
import { FeatureService } from 'src/app/services/feature.service';
import { SubscriptionDTO } from 'src/app/interfaces/subscription';
import { SubscriptionService } from 'src/app/services/subscription.service';
import { UserSubscriptionDTO, UserSubscriptionNumberDTO, UserSubscriptionWriteDTO } from 'src/app/interfaces/userSubscription';
import { UserSubscriptionService } from 'src/app/services/user-subscription.service';
import { FeatureSubscriptionDTO, FeatureSubscriptionWriteDTO } from 'src/app/interfaces/featureSubscription';
import { FeatureSubscriptionService } from 'src/app/services/feature-subscription.service';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule
  ],
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss']
})
export class AdminComponent implements OnInit {

  features: FeatureDTO[] = [];
  subscriptions: SubscriptionDTO[] = [];
  userSubscriptions: UserSubscriptionDTO[] = [];
  userSubscriptionNumbers: UserSubscriptionNumberDTO[] = [];
  featureSubscriptions: FeatureSubscriptionDTO[] = [];

  featurePage: number = 0;
  featureSize: number = 50;
  subscriptionPage: number = 0;
  subscriptionSize: number = 50;
  userSubscriptionPage: number = 0;
  userSubscriptionSize: number = 50;

  // Tabel kolommen (aangepast aan de UserSubscriptionDTO velden)
  userColumns: string[] = ['email', 'subscription', 'lastLogin'];
  usersDataSource = new MatTableDataSource<UserSubscriptionDTO>();

  constructor(
    private featureService: FeatureService,
    private subscriptionService: SubscriptionService,
    private userSubscriptionService: UserSubscriptionService,
    private featureSubscriptionService: FeatureSubscriptionService
  ) {}

  ngOnInit(): void {
    this.loadAllData();

    // Filteren op e-mailadres
    this.usersDataSource.filterPredicate = (data: UserSubscriptionDTO, filter: string) => {
      return (data.email || '').toLowerCase().includes(filter.trim().toLowerCase());
    };
  }

  async loadAllData() {
    try {
      await Promise.all([
        this.getFeatures(),
        this.getSubscriptions(),
        this.getUserSubscriptions(),
        this.getUserSubscriptionNumbers(),
        this.getFeatureSubscriptions()
      ]);
    } catch (error) {
      console.error('Error loading admin data:', error);
    }
  }

  // --- TAB 1: USERS ---
  applyUserFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.usersDataSource.filter = filterValue.trim().toLowerCase();
  }

  async onUserSubscriptionChange(userSubscription: UserSubscriptionDTO, newSubscriptionId: number) {
    try {
      const payload: UserSubscriptionWriteDTO = {
        userId: userSubscription.userId,
        subscriptionId: newSubscriptionId,
        startDate: userSubscription.startDate,
        endDate: userSubscription.endDate
      };

      await firstValueFrom(this.userSubscriptionService.saveUserSubscription(userSubscription.id, payload as any));
      
      // Update de lokale state
      userSubscription.subscriptionId = newSubscriptionId;
      const selectedSub = this.subscriptions.find(s => s.id === newSubscriptionId);
      if (selectedSub) {
        userSubscription.subscriptionName = selectedSub.name;
      }

      await this.getUserSubscriptionNumbers();
    } catch (error) {
      console.error('Error updating the subscription of the user:', error);
      await this.getUserSubscriptions(); 
    }
  }

  getTotalUsersCount(): number {
    return this.usersDataSource.data.length;
  }

  getUsersCountBySubscription(subscriptionName: string): number {
    const found = this.userSubscriptionNumbers.find(u => u.subscriptionName === subscriptionName);
    return found ? found.numberOf : 0;
  }

  // --- TAB 2: FEATURES ---
  async onFeatureToggle(feature: FeatureDTO) {
    try {
      await firstValueFrom(this.featureService.toggleFeatureEnabled(feature.id, feature.enabled));
    } catch (error) {
      console.error('Error toggling feature:', error);
      feature.enabled = !feature.enabled;
    }
  }

  // --- TAB 3: SUBSCRIPTIONS MATRIX ---
  isFeatureEnabledForSubscription(subscriptionId: number, featureId: number): boolean {
    return this.featureSubscriptions.some(
      fs => fs.subscriptionId === subscriptionId && fs.featureId === featureId
    );
  }

  async onSubscriptionFeatureToggle(subscriptionId: number, featureId: number, enabled: boolean) {
    const existingMapping = this.featureSubscriptions.find(
      fs => fs.subscriptionId === subscriptionId && fs.featureId === featureId
    );

    try {
      if (existingMapping) {
        await firstValueFrom(this.featureSubscriptionService.deleteFeatureSubscription(existingMapping.id));
      } else {
        const newPayload: FeatureSubscriptionWriteDTO = {
          featureId: featureId,
          subscriptionId: subscriptionId
        };
        await firstValueFrom(this.featureSubscriptionService.createFeatureSubscription(newPayload as any));
      }
      await this.getFeatureSubscriptions();
    } catch (error) {
      console.error('Error updating feature-subscription matrix:', error);
      await this.getFeatureSubscriptions();
    }
  }

  // --- HTTP FETCHING ---
  async getFeatures() {
    const data = await firstValueFrom(this.featureService.getFeatures(this.featurePage, this.featureSize));
    this.features = data?.content || data || [];
  }

  async getFeatureSubscriptions() {
    this.featureSubscriptions = await firstValueFrom(this.featureSubscriptionService.getFeatureSubscriptions()) || [];
  }

  async getSubscriptions() {
    const data = await firstValueFrom(this.subscriptionService.getSubscriptions(this.subscriptionPage, this.subscriptionSize));
    this.subscriptions = data?.content || data || [];
  }

  async getUserSubscriptions() {
    const data = await firstValueFrom(this.userSubscriptionService.getUserSubscriptions(this.userSubscriptionPage, this.userSubscriptionSize));
    this.userSubscriptions = data?.content || data || [];
    this.usersDataSource.data = this.userSubscriptions;
  }

  async getUserSubscriptionNumbers() {
    this.userSubscriptionNumbers = await firstValueFrom(this.userSubscriptionService.getUserSubscriptionNumbers()) || [];
  }
}